import React, { useState } from 'react';
import {
  Bar,
  Button,
  CheckBox,
  CheckBoxDomRef,
  Dialog,
  Table,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Ui5CustomEvent
} from '@ui5/webcomponents-react';
import { useTranslation } from 'react-i18next';
import { useUserViewStore } from 'stores/UserViewStore';
import { useSubscribeViewsMutation } from 'generated/graphql';
import { useStyles } from './ManageSubscriptionsDialog.jss';
import { isStandardView } from 'components/ManageQueries/Filters/StandardView';
import { UserMessage } from 'components/shared/UserMessage';

import ButtonDesign from '@ui5/webcomponents/dist/types/ButtonDesign.js';

type ManageSubscriptionsDialogProps = {
  isOpen: boolean;
  setIsSubscriptionsDialogOpen: (isOpen: boolean) => void;
};

export const ManageSubscriptionsDialog: React.FC<ManageSubscriptionsDialogProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { isOpen, setIsSubscriptionsDialogOpen } = props;
  const { setViews } = useUserViewStore();

  const views = useUserViewStore((state) => state.views);

  const [selectedViews, setSelectedViews] = useState(
    views?.reduce<Record<string, boolean>>((acc, view) => {
      acc[view.id] = view.isSubscribed;
      return acc;
    }, {})
  );

  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);

  const [, updateViewsSubscriptions] = useSubscribeViewsMutation();

  const handleSubscribeViewClick = (
    event: Ui5CustomEvent<CheckBoxDomRef, never>,
    viewId: string
  ) => {
    const isChecked = event.target.checked;
    setSelectedViews((prev) => ({
      ...prev,
      [viewId]: isChecked
    }));
  };

  const onSave = async () => {
    const updatedSubscriptions = Object.entries(selectedViews || {}).map(([id, isSubscribed]) => ({
      id,
      isSubscribed
    }));

    await updateViewsSubscriptions({ searchFilters: updatedSubscriptions }).then((result) => {
      setIsSubscriptionsDialogOpen(false);
      if (result.error) {
        setIsErrorDialogOpen(true);
        return;
      }
      setViews(result.data?.mutation);
    });
  };

  const clearStateAndCloseDialog = () => {
    setSelectedViews(
      views?.reduce<Record<string, boolean>>((acc, view) => {
        acc[view.id] = view.isSubscribed;
        return acc;
      }, {})
    );
    setIsSubscriptionsDialogOpen(false);
  };

  const onCancel = () => {
    clearStateAndCloseDialog();
  };

  return (
    <>
      <Dialog
        id="manage-subscriptions-dialog"
        open={isOpen}
        className={classes.dialog}
        onBeforeClose={() => clearStateAndCloseDialog()}
        data-testid="manage-subscriptions-dialog"
        data-help-id="manage-subscriptions-dialog"
        headerText={t('subscription-dialog-title')}
        footer={
          <Bar
            endContent={
              <>
                <Button
                  onClick={onSave}
                  design={ButtonDesign.Emphasized}
                  id="manage-subscriptions-dialog-save-button"
                  key="manage-subscriptions-dialog-save-button"
                  data-testid="manage-subscriptions-dialog-save-button"
                >
                  {t('subscription-dialog-save-button')}
                </Button>
                <Button
                  onClick={onCancel}
                  design={ButtonDesign.Transparent}
                  id="manage-subscription-dialog-cancel-button"
                  key="manage-subscription-dialog-cancel-button"
                  data-testid="manage-subscription-dialog-cancel-button"
                >
                  {t('subscription-dialog-cancel-button')}
                </Button>
              </>
            }
          />
        }
      >
        {isOpen && (
          <Table
            data-testid="subscriptions-table"
            headerRow={
              <TableHeaderRow>
                <TableHeaderCell className={classes.tableViewColumn}>
                  <span data-help-id="subscriptions-table-view-column">
                    {t('subscription-dialog-view-column-title')}
                  </span>
                </TableHeaderCell>
                <TableHeaderCell>
                  <span
                    className={classes.tableSubscribeColumn}
                    data-help-id="subscriptions-table-subscribe-column"
                  >
                    {t('subscription-dialog-subscribe-column-title')}
                  </span>
                </TableHeaderCell>
              </TableHeaderRow>
            }
          >
            {views?.map((view) => (
              <TableRow key={view.id}>
                <TableCell className={isStandardView(view) ? classes.standardViewTitle : undefined}>
                  {view.name}
                </TableCell>
                <TableCell className={classes.tableSubscribeColumn}>
                  <CheckBox
                    readonly={isStandardView(view)}
                    data-testid={`view-subscription-checkbox-${view.id}`}
                    onChange={(event) => handleSubscribeViewClick(event, view.id)}
                    checked={selectedViews?.[view.id] || false}
                    wrappingType="None"
                  />
                </TableCell>
              </TableRow>
            ))}
          </Table>
        )}
      </Dialog>
      <UserMessage
        isVisible={isErrorDialogOpen}
        resultMessage={{ isSuccess: false, message: t('subscription-error-message') }}
        onResetUserMessage={() => setIsErrorDialogOpen(false)}
      />
    </>
  );
};
